// Types and capability flags for the Loop hardware SDK global, injected
// by the Loop device's WebView as `window.Loop`. Desktop browsers don't
// have it — hasLoop is false there, and feature detection below gates all
// calls so the app runs on desktop unchanged.

import type { LoopQuat } from './utils/quaternion';

export interface LoopHaptics {
  isSupported(): boolean;
  pulse(strength: number, durationMs?: number): void;
}

export interface LoopButtonEvent {
  state: 'down' | 'up';
}

export interface LoopButtons {
  on(button: string, cb: (event: LoopButtonEvent) => void): void;
}

export interface LoopMotionData {
  orientation?: LoopQuat;
}

export interface LoopMotionSubscription {
  on(event: 'data', cb: (data: LoopMotionData) => void): void;
}

export interface LoopMotionOptions {
  frequency?: number;
  smoothing?: number;
}

export interface LoopMotion {
  isSupported(): boolean;
  start(options?: LoopMotionOptions): Promise<LoopMotionSubscription>;
}

export interface LoopSystem {
  quit(): void;
  launch(appId: string): Promise<void>;
  launchExternal(appId: string): Promise<void>;
  // Native free-rotate (display auto-leveling). Available on Loop SDK 0.3.0+.
  // Return shape matches the native bridge contract.
  setFreeRotate(enabled: boolean): { success: boolean };
  isFreeRotateEnabled(): boolean;
}

export interface LoopLed {
  [key: string]: unknown;
}

export interface LoopGlobal {
  haptics?: LoopHaptics;
  buttons?: LoopButtons;
  motion?: LoopMotion;
  system?: LoopSystem;
  led?: LoopLed;
}

// Single, isolated `window as any` cast here and nowhere else.
const w = window as unknown as { Loop?: LoopGlobal };
export const loop: LoopGlobal | null = w.Loop ?? null;

export const hasLoop    = loop !== null;
export const hasMotion  = hasLoop && !!loop!.motion && typeof loop!.motion.isSupported === 'function' && loop!.motion.isSupported();
export const hasButtons = hasLoop && !!loop!.buttons;
export const hasHaptics = hasLoop && !!loop!.haptics && typeof loop!.haptics.isSupported === 'function' && loop!.haptics.isSupported();
export const hasLed     = hasLoop && !!loop!.led;
export const hasSystemFreeRotate =
  hasLoop
  && !!loop!.system
  && typeof (loop!.system as Partial<LoopSystem>).setFreeRotate === 'function'
  && typeof (loop!.system as Partial<LoopSystem>).isFreeRotateEnabled === 'function';
